import * as Phaser from 'phaser';
import { GAME_CONFIG } from '../config';
import {
  type Profile,
  createProfile,
  deleteProfile,
  getAllProfiles,
  setActiveProfile,
} from '../profiles/ProfileManager';

const { width, height } = GAME_CONFIG;

interface ProfileItem {
  id: string;
  y: number;
  h: number;
}

export class MenuScene extends Phaser.Scene {
  private profiles: Profile[] = [];
  private selectedProfileId: string | null = null;
  private scrollOffset = 0;
  private readonly itemH = 72;
  private readonly visible = 5;
  private listY = 140;
  private playBtnGraphics!: Phaser.GameObjects.Graphics;
  private playBtnText!: Phaser.GameObjects.Text;
  private headerText!: Phaser.GameObjects.Text;
  private profileItems: ProfileItem[] = [];
  private formContainer!: Phaser.GameObjects.Container;
  private nameInput: string = '';
  private ageInput: number = 7;
  private nameDisplay!: Phaser.GameObjects.Text;
  private ageDisplay!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'MenuScene' });
  }

  create(): void {
    this.selectedProfileId = null;
    this.scrollOffset = 0;
    this.profileItems = [];
    this.profiles = getAllProfiles();

    // ── Fondo ──────────────────────────────────────────────────────
    const bg = this.add.graphics();
    bg.fillStyle(GAME_CONFIG.colors.background, 1);
    bg.fillRect(0, 0, width, height);

    // ── Título ──────────────────────────────────────────────────────
    this.add.text(width / 2, 30, 'EduBlox', {
      fontSize: '40px', fontFamily: 'Arial, sans-serif',
      color: '#ffcc80', fontStyle: 'bold',
      stroke: '#3e2723', strokeThickness: 4,
    }).setOrigin(0.5, 0);

    this.add.text(width / 2, 78, 'Block Puzzle Educativo', {
      fontSize: '14px', fontFamily: 'Arial, sans-serif', color: '#bcaaa4',
    }).setOrigin(0.5, 0);

    this.headerText = this.add.text(width / 2, 110, 'Selecciona tu perfil', {
      fontSize: '18px', fontFamily: 'Arial, sans-serif',
      color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5, 0);

    // ── Renderizar perfiles ─────────────────────────────────────────
    this.renderProfiles();

    // ── Botón Nuevo Jugador ─────────────────────────────────────────
    const newBtnY = this.listY + this.visible * this.itemH + 20;
    this.add.graphics()
      .fillStyle(0x4caf50, 1)
      .fillRoundedRect(width / 2 - 90, newBtnY - 20, 180, 42, 10);
    this.add.text(width / 2, newBtnY, 'Nuevo Jugador', {
      fontSize: '17px', fontFamily: 'Arial, sans-serif',
      color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5);

    // ── Botón Jugar ─────────────────────────────────────────────────
    const playBtnY = height - 40;
    this.playBtnGraphics = this.add.graphics();
    this.playBtnText = this.add.text(width / 2, playBtnY, 'Jugar', {
      fontSize: '20px', fontFamily: 'Arial, sans-serif',
      color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5).setAlpha(0.4);
    this.drawPlayBtn(false);

    // ── INPUT GLOBAL — UN solo listener para todo ───────────────────
    // Esto es lo que arregla el táctil en móvil
    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      const px = pointer.x;
      const py = pointer.y;

      // 1. Verificar si tocó un perfil
      for (const item of this.profileItems) {
        if (px >= 20 && px <= width - 20 && py >= item.y && py <= item.y + item.h) {
          // Tocar la X de eliminar (esquina superior derecha del item)
          if (px >= width - 54 && px <= width - 18 && py >= item.y + item.h - 30 && py <= item.y + item.h - 6) {
            if (confirm(`¿Eliminar perfil?`)) {
              deleteProfile(item.id);
              if (this.selectedProfileId === item.id) {
                this.selectedProfileId = null;
                this.drawPlayBtn(false);
              }
              this.renderProfiles();
            }
            return;
          }
          // Seleccionar perfil
          this.selectedProfileId = item.id;
          this.headerText.setText(`Jugador: ${this.profiles.find(p => p.id === item.id)?.name || ''}`);
          this.drawPlayBtn(true);
          this.renderProfiles();
          return;
        }
      }

      // 2. Botón Jugar (parte inferior)
      if (px >= width / 2 - 100 && px <= width / 2 + 100 &&
          py >= playBtnY - 24 && py <= playBtnY + 24) {
        if (this.selectedProfileId) {
          setActiveProfile(this.selectedProfileId);
          this.scene.start('GameScene', { profileId: this.selectedProfileId });
        }
        return;
      }

      // 3. Botón Nuevo Jugador
      if (px >= width / 2 - 90 && px <= width / 2 + 90 &&
          py >= newBtnY - 20 && py <= newBtnY + 22) {
        this.showForm();
        return;
      }

      // 4. Si el formulario está visible, manejar sus botones
      if (this.formContainer.visible) {
        this.handleFormTap(px, py);
      }
    });
  }

  // ── Renderizar perfiles ──────────────────────────────────────────
  private renderProfiles(): void {
    // Limpiar todo
    this.profileItems = [];
    this.profiles = getAllProfiles();

    // Eliminar nodos viejos (excepto los que no son perfiles)
    this.children.getAll().forEach(child => {
      if (child.getData('isProfile') === true) {
        child.destroy();
      }
    });

    const start = this.scrollOffset;
    for (let i = start; i < this.profiles.length && i < start + this.visible; i++) {
      this.drawProfile(this.profiles[i], i - start);
    }
  }

  private drawProfile(profile: Profile, index: number): void {
    const y = this.listY + index * this.itemH;
    const isSelected = this.selectedProfileId === profile.id;
    const rowH = this.itemH - 4;

    // Background
    const bg = this.add.graphics();
    bg.fillStyle(isSelected ? 0x5d4037 : 0x4e342e, 1);
    bg.fillRoundedRect(20, y, width - 40, rowH, 8);
    if (isSelected) {
      bg.lineStyle(2, 0xffcc80, 1);
      bg.strokeRoundedRect(20, y, width - 40, rowH, 8);
    }
    bg.setData('isProfile', true);

    // Name
    this.add.text(36, y + 8, profile.name, {
      fontSize: '17px', fontFamily: 'Arial, sans-serif',
      color: '#ffffff', fontStyle: 'bold',
    }).setData('isProfile', true);

    // Detail
    this.add.text(36, y + 30, `${profile.age} años · ${profile.gamesPlayed} partidas`, {
      fontSize: '12px', fontFamily: 'Arial, sans-serif', color: '#bcaaa4',
    }).setData('isProfile', true);

    // Score
    this.add.text(width - 36, y + 10, `🏆 ${profile.highScore}`, {
      fontSize: '15px', fontFamily: 'Arial, sans-serif',
      color: '#ffcc80', fontStyle: 'bold',
    }).setOrigin(1, 0).setData('isProfile', true);

    // Delete X
    this.add.text(width - 36, y + rowH - 18, '✕', {
      fontSize: '14px', fontFamily: 'Arial, sans-serif',
      color: '#ef5350', fontStyle: 'bold',
    }).setOrigin(0.5).setData('isProfile', true);

    // Registrar para detección táctil
    this.profileItems.push({ id: profile.id, y, h: rowH });
  }

  // ── Botón Jugar ─────────────────────────────────────────────────
  private drawPlayBtn(active: boolean): void {
    this.playBtnGraphics.clear();
    if (active) {
      this.playBtnGraphics.fillStyle(0x2196f3, 1);
      this.playBtnGraphics.fillRoundedRect(width / 2 - 100, height - 64, 200, 48, 12);
      this.playBtnText.setAlpha(1);
    } else {
      this.playBtnGraphics.fillStyle(0x555555, 1);
      this.playBtnGraphics.fillRoundedRect(width / 2 - 100, height - 64, 200, 48, 12);
      this.playBtnText.setAlpha(0.4);
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  //  FORMULARIO DE CREACIÓN
  // ═══════════════════════════════════════════════════════════════════
  private showForm(): void {
    this.nameInput = '';
    this.ageInput = 7;
    this.buildForm();
    this.formContainer.setVisible(true);
    this.formContainer.setDepth(100);
  }

  private hideForm(): void {
    this.formContainer.setVisible(false);
    this.formContainer.removeAll(true);
  }

  private formRects: { x: number; y: number; w: number; h: number; action: string }[] = [];

  private buildForm(): void {
    this.formContainer = this.add.container(0, 0);
    this.formRects = [];

    // Overlay
    const overlay = this.add.graphics();
    overlay.fillStyle(0x000000, 0.7);
    overlay.fillRect(0, 0, width, height);
    this.formContainer.add(overlay);

    // Panel
    const panel = this.add.graphics();
    panel.fillStyle(0x4e342e, 1);
    panel.fillRoundedRect(width / 2 - 160, height / 2 - 160, 320, 320, 16);
    panel.lineStyle(2, 0x8d6e63, 1);
    panel.strokeRoundedRect(width / 2 - 160, height / 2 - 160, 320, 320, 16);
    this.formContainer.add(panel);

    // Title
    this.formContainer.add(this.add.text(width / 2, height / 2 - 130, 'Nuevo Jugador', {
      fontSize: '22px', fontFamily: 'Arial, sans-serif', color: '#ffcc80', fontStyle: 'bold',
    }).setOrigin(0.5));

    // Name label
    this.formContainer.add(this.add.text(width / 2 - 120, height / 2 - 85, 'Nombre:', {
      fontSize: '16px', fontFamily: 'Arial, sans-serif', color: '#ffffff',
    }));

    // Name input bg
    const nameBg = this.add.graphics();
    nameBg.fillStyle(0x3e2723, 1);
    nameBg.fillRoundedRect(width / 2 - 120, height / 2 - 70, 240, 36, 6);
    this.formContainer.add(nameBg);

    // Name input text
    this.nameDisplay = this.add.text(width / 2 - 110, height / 2 - 52, '', {
      fontSize: '18px', fontFamily: 'Arial, sans-serif', color: '#ffffff',
    });
    this.formContainer.add(this.nameDisplay);

    this.formRects.push({ x: width / 2 - 120, y: height / 2 - 70, w: 240, h: 36, action: 'name' });

    // Age label
    this.formContainer.add(this.add.text(width / 2 - 120, height / 2 - 20, 'Edad:', {
      fontSize: '16px', fontFamily: 'Arial, sans-serif', color: '#ffffff',
    }));

    this.ageDisplay = this.add.text(width / 2, height / 2 + 10, `${this.ageInput} años`, {
      fontSize: '20px', fontFamily: 'Arial, sans-serif', color: '#ffcc80', fontStyle: 'bold',
    }).setOrigin(0.5);
    this.formContainer.add(this.ageDisplay);

    // Minus
    this.formRects.push({ x: width / 2 - 68, y: height / 2 - 8, w: 36, h: 36, action: 'age_minus' });
    this.formContainer.add(this.add.text(width / 2 - 50, height / 2 + 10, '-', {
      fontSize: '24px', fontFamily: 'Arial, sans-serif', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5));

    // Plus
    this.formRects.push({ x: width / 2 + 32, y: height / 2 - 8, w: 36, h: 36, action: 'age_plus' });
    this.formContainer.add(this.add.text(width / 2 + 50, height / 2 + 10, '+', {
      fontSize: '24px', fontFamily: 'Arial, sans-serif', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5));

    // Create button
    this.formRects.push({ x: width / 2 - 80, y: height / 2 + 65, w: 160, h: 44, action: 'create' });
    const createBg = this.add.graphics();
    createBg.fillStyle(0x4caf50, 1);
    createBg.fillRoundedRect(width / 2 - 80, height / 2 + 65, 160, 44, 10);
    this.formContainer.add(createBg);
    this.formContainer.add(this.add.text(width / 2, height / 2 + 87, 'Crear Perfil', {
      fontSize: '18px', fontFamily: 'Arial, sans-serif', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5));

    // Cancel button
    this.formRects.push({ x: width / 2 - 60, y: height / 2 + 120, w: 120, h: 34, action: 'cancel' });
    const cancelBg = this.add.graphics();
    cancelBg.fillStyle(0x757575, 1);
    cancelBg.fillRoundedRect(width / 2 - 60, height / 2 + 120, 120, 34, 8);
    this.formContainer.add(cancelBg);
    this.formContainer.add(this.add.text(width / 2, height / 2 + 137, 'Cancelar', {
      fontSize: '14px', fontFamily: 'Arial, sans-serif', color: '#ffffff',
    }).setOrigin(0.5));

    this.formContainer.setVisible(false);
  }

  private handleFormTap(px: number, py: number): void {
    for (const r of this.formRects) {
      if (px >= r.x && px <= r.x + r.w && py >= r.y && py <= r.y + r.h) {
        switch (r.action) {
          case 'name':
            const val = prompt('Nombre del jugador:');
            if (val) {
              this.nameInput = val.trim().substring(0, 20);
              this.nameDisplay.setText(this.nameInput);
            }
            return;
          case 'age_minus':
            this.ageInput = Math.max(4, this.ageInput - 1);
            this.ageDisplay.setText(`${this.ageInput} años`);
            return;
          case 'age_plus':
            this.ageInput = Math.min(12, this.ageInput + 1);
            this.ageDisplay.setText(`${this.ageInput} años`);
            return;
          case 'create':
            if (!this.nameInput.trim()) {
              this.showToast('¡Escribe un nombre!');
              return;
            }
            try {
              createProfile(this.nameInput.trim(), this.ageInput, `${this.ageInput}°`);
              this.hideForm();
              this.renderProfiles();
              this.showToast(`¡Perfil "${this.nameInput}" creado!`);
            } catch (e) {
              this.showToast(e instanceof Error ? e.message : 'Error');
            }
            return;
          case 'cancel':
            this.hideForm();
            return;
        }
      }
    }
  }

  private showToast(msg: string): void {
    const t = this.add.text(width / 2, height - 100, msg, {
      fontSize: '16px', fontFamily: 'Arial, sans-serif',
      color: '#ffffff', backgroundColor: '#333333',
      padding: { x: 12, y: 6 },
    }).setOrigin(0.5).setDepth(200);
    this.tweens.add({
      targets: t, alpha: 0, y: height - 140,
      delay: 1500, duration: 500,
      onComplete: () => t.destroy(),
    });
  }
}